"use client";

import React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import debounce from "lodash/debounce";
import { Link, useNavigate } from "react-router-dom";
import Api from "../../config/Api";
import Loader from "../Widget/Loader";
import { BsEye, BsFillPencilFill } from "react-icons/bs";
import TooltipWidget from "../Widget/TooltipWidget";

const ROW_HEIGHT = 70;
const PER_PAGE = 50;

const UpdateForm = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [totalPageAndRecords, setTotalPageAndRecords] = useState({
    totalPage: 0,
    totalRecords: 0,
  });
  const [error, setError] = useState("");
  const totalFetched = useRef(0);

  const fetchData = useCallback(
    async (pageNum = 1, reset = false, searchTerm = search) => {
      if (reset) {
        setIsSearching(true);
      } else {
        setIsLoading(true);
      }

      setError("");

      try {
        const res = await Api(`get-downloaded-update-form?page=${pageNum}&per_page=${PER_PAGE}&search=${searchTerm}`);

        if (res?.data?.status === 1) {
          const newData = res.data.data.data;
          setData((prev) => (reset ? newData : [...prev, ...newData]));
          setTotalPageAndRecords({
            totalPage: res.data.data.last_page,
            totalRecords: res.data.data.total,
          });
          totalFetched.current = reset
            ? newData.length
            : totalFetched.current + newData.length;
          setHasMore(res.data.data.next_page_url !== null);
          setPage(pageNum + 1);
        } else {
          setError(res?.data?.message || "Failed to fetch data");
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          setError("Something went wrong while loading data.");
        }
      } finally {
        setIsLoading(false);
        setIsSearching(false);
      }
    },
    [search]
  );

  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      setPage(1);
      totalFetched.current = 0;
      setData([]);
      setSearch(searchValue);
      fetchData(1, true, searchValue);
    }, 100),
    [fetchData]
  );

  useEffect(() => {
    fetchData(1, true);
  }, []);

  
  const handleSearchChange = (e) => {
    const value = e.target.value;
    debouncedSearch(value);
  };

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore && data.length > 0) {
      console.log("Loading more data, page:", page);
      fetchData(page);
    }
  }, [fetchData, page, isLoading, hasMore, data.length]);

  const Row = ({ index, style }) => {
    const item = data[index];

    if (hasMore && index === data.length - 5 && !isLoading) {
      loadMore();
    }

    if (!item) {
      return (
        <div
          style={{
            ...style,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div className="text-muted">Loading...</div>
        </div>
      );
    }

    return (
      <div
        style={{
          ...style,
          display: "flex",
          alignItems: "center",
          padding: "0.75rem 0.5rem",
          borderBottom: "1px solid #eee",
          fontSize: "0.800rem",
        }}
        className="hover:bg-gray-50"
      >
        <div
          style={{ width: "60px", textAlign: "center" }}
          className="text-muted"
        >
          {index + 1}
        </div>
        <div
          style={{ flex: 1, paddingRight: "0.5rem" }}
          className="font-medium"
        >
          {item.application_id}
        </div>
        <div style={{ flex: 1, paddingRight: "0.5rem" }}>
          <img
          src={item.applicant_default_img}
          alt="Applicant"
          style={{
            height: "60px",
            width: "60px",
            objectFit: "cover",
            margin:"10px",
            borderRadius:"5px"
          }}
        />
        </div>
        <div
          style={{ flex: 1, paddingRight: "0.5rem" }}
          className="text-blue-600"
        >
          {item.applicant_name || "N/A"}
          <br />
          {item.mobile || "N/A"}
        </div>
        <div
          style={{ flex: 1, paddingRight: "0.5rem" }}
          className="font-mono text-sm"
        >
          {item.district?.district_name || "N/A"}
        </div>
        <div style={{ flex: 1, paddingRight: "0.5rem" }}>
          {item.aadhar_no || "N/A"}
        </div>
        <div style={{ flex: 1, paddingRight: "0.5rem" }}>
          <span className="badge bg-success">{item.status === "6" 
            ? "Final Approved" 
            : item.status === "8" 
              ? "Under Process" 
              : "N/A"}</span>
        </div>
        <div style={{ flex: 0.8 }}>
          <span
            className={`px-2 py-1 cursor-pointer rounded-full text-xs bg-green-100 text-green-800`}
          >
           <TooltipWidget title="Edit SDRF">
            <button
              className="btn btn-primary  btn-sm"
              onClick={() =>
                navigate("/sdrf-1", {
                  state: {
                    id: item.id,
                    isEdit: true,
                  },
                })
              }
            >
              <BsFillPencilFill />
            </button>
          </TooltipWidget>  
          </span>
        </div>
      </div>
    );
  };

  const TableHeader = () => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: "0.5rem 0.5rem",
        fontWeight: "600",
        borderBottom: "2px solid #e5e7eb",
        backgroundColor: "#f9fafb",
        fontSize: "0.875rem",
      }}
    >
      <div style={{ width: "60px", textAlign: "center" }}>#</div>
      <div style={{ flex: 1, paddingRight: "0.5rem" }}>Application ID</div>
      <div style={{ flex: 1, paddingRight: "0.5rem" }}>Applicant Passport Photo</div>
      <div style={{ flex: 1, paddingRight: "0.5rem" }}>Applicant Info</div>
      <div style={{ flex: 1, paddingRight: "0.5rem" }}>District</div>
      <div style={{ flex: 1, paddingRight: "0.5rem" }}>Aadhar No</div>
      <div style={{ flex: 1, paddingRight: "0.5rem" }}>Status</div>
      <div style={{ flex: 1, paddingRight: "0.5rem" }}>Action</div>
    </div>
  );
  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border">
      {/* Header */}
      <div className="d-md-flex justify-content-between mb-3 align-items-center">
        <h6 className="text-dark m-0">
          Update Forms
        </h6>
        <div className="d-flex gap-2 flex-wrap">
          <div>
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ width: "250px" }}
              placeholder="Search by ID, email, district..."
              value={search}
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {/* Table */}
      {data.length > 0 ? (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <TableHeader />
          <div style={{ height: "400px" }}>
            <AutoSizer>
              {({ height, width }) => (
                <List
                  height={height}
                  itemCount={data.length}
                  itemSize={ROW_HEIGHT}
                  width={width}
                >
                  {Row}
                </List>
              )}
            </AutoSizer>
          </div>

          {isLoading && (
            <div className="border-t border-gray-200 p-4">
              <Loader title="Loading..." />
            </div>
          )}

          <div className="d-flex justify-content-end align-items-center p-4 border-t bg-gray-50 text-sm">
     
            {/* <button
              className="btn btn-light btn-sm me-2 ms-4 border"
              onClick={() => handleManualPageChange(page - 1)}
              disabled={page <= 1}
            >
              <FaArrowLeft />
            </button> */}
            <span className="text-sm text-gray-500">
              Total Records: {totalPageAndRecords.totalRecords.toLocaleString()} | Page {page} of {totalPageAndRecords.totalPage}
            </span>

            {/* <button
              className="btn btn-light btn-sm ms-2 border"
              onClick={() => handleManualPageChange(page + 1)}
              disabled={!hasMore}
            >
              <FaArrowRight />
            </button> */}
          </div>
        </div>
      ) : isSearching || isLoading ? (
        <Loader title="Loading..." />
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg mb-2">No records found</div>
          <div className="text-gray-400 text-sm">
            {search ? `No results for "${search}"` : "No data available"}
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdateForm;
